// src/components/UserTable.jsx

import React from 'react';
import PropTypes from 'prop-types';
import ActionButton from '../Common/ActionButton';
import './UserTable.css';
import { useTranslation } from 'react-i18next'; // Import the i18n hook
import { useLanguageSettings } from '../Common/LanguageSettingsContext'; // Import LanguageSettings hook
import { useColorSettings } from '../Common/ColorSettingsContext'; // Import ColorSettings hook
import Spinner from '../Common/Spinner'; // Import Spinner component

const UserTable = ({ users, isMobile, onEditUser, onDeleteUser, onResetPassword }) => {
  const { t } = useTranslation(); // Initialize the i18n translation hook

  // Consume LanguageSettings and ColorSettings contexts
  const { language, updateLanguage, loading: languageLoading } = useLanguageSettings();
  const { appSettings, loading: colorLoading } = useColorSettings();

  // Combine loading states
  if (colorLoading || languageLoading) {
    return (
      <div className="table-responsive">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="users-table">
        <thead>
          <tr>
            <th>{t('userTable.fullName')}</th>
            <th className="hide-on-mobile">{t('userTable.email')}</th>
            <th className="hide-on-mobile">{t('userTable.role')}</th>
            <th>{t('userTable.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan="4" className="no-data" aria-live="polite">
                {t('userTable.noUsers')}
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <tr key={user.id}>
                <td data-label={t('userTable.fullName')}>{user.fullName || t('userTable.na')}</td>
                <td data-label={t('userTable.email')} className="hide-on-mobile">
                  {user.email}
                </td>
                <td data-label={t('userTable.role')} className="hide-on-mobile">
                  {user.role === 'admin' ? t('userTable.admin') : t('userTable.user')}
                </td>
                <td data-label={t('userTable.actions')}>
                  <ActionButton
                    onClick={() => onEditUser(user)}
                    label={t('userTable.editUser', { fullName: user.fullName })}
                    icon="faPencil"
                    isMobile={isMobile}
                    colorType="primary"
                    ariaLabel={t('userTable.editUserAria', { fullName: user.fullName })}
                  />
                  <ActionButton
                    onClick={() => onDeleteUser(user)}
                    label={t('userTable.deleteUser', { fullName: user.fullName })}
                    icon="faTrash"
                    isMobile={isMobile}
                    colorType="primary"
                    ariaLabel={t('userTable.deleteUserAria', { fullName: user.fullName })}
                  />
                  <ActionButton
                    onClick={() => onResetPassword(user)}
                    label={t('userTable.resetPassword', { fullName: user.fullName })}
                    icon="faKey"
                    isMobile={isMobile}
                    colorType="primary"
                    ariaLabel={t('userTable.resetPasswordAria', { fullName: user.fullName })}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

UserTable.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  isMobile: PropTypes.bool.isRequired,
  onEditUser: PropTypes.func.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
};

export default UserTable;
